import { useEffect, useMemo } from 'react';
import { useLazyGetProviderAvailabilityQuery } from '~/store/services/schedule';
import {
  extractProviderIds,
  getAvailabilityQueryEndDate,
  getAvailabilityQueryStartDate,
} from '../utils';
import { useDatadog } from '~/lib/hooks/useDatadog';

export type ProviderAvailabilityProps = {
  admitDate: string;
  appointmentType: string;
  recommendationsData: GetProviderAssignmentRecommendationsResponse;
  patientId: string;
  skip: boolean;
  supportIds?: string[];
};

/**
 * Fetches provider availability for providers found in the provider assignments
 * response. Only triggers the API call once all required data is present, and
 * re-fetches when the admit date or provider list changes.
 */
const useProviderAvailability = ({
  admitDate,
  appointmentType,
  recommendationsData,
  patientId,
  skip,
  supportIds,
}: ProviderAvailabilityProps) => {
  const [
    fetchProviderAvailability,
    { data: availabilityData, isError, isFetching },
  ] = useLazyGetProviderAvailabilityQuery();
  const { logError } = useDatadog();

  // Serialize provider IDs to a stable string so the effect only re-runs
  // when the actual set of IDs changes, not on every render.
  const providerIdKey = useMemo(
    () =>
      [...new Set(extractProviderIds(recommendationsData))]
        .sort((a, b) => a.localeCompare(b))
        .join(','),
    [recommendationsData],
  );

  const availableSlots = useMemo(
    () =>
      (availabilityData?.data?.availableSlots ?? []).flatMap((slot) =>
        (slot.users ?? []).map(user => ({
          endTime: slot.end,
          providerId: user.id,
          startTime: slot.start,
        }))
      ),
    [availabilityData?.data?.availableSlots],
  );

  // Serialize support IDs to a stable string for the same reason.
  const supportIdKey = useMemo(
    () =>
      [...new Set(supportIds ?? [])]
        .sort((a, b) => a.localeCompare(b))
        .join(','),
    [supportIds],
  );

  useEffect(() => {
    if (skip || !admitDate || !appointmentType || !providerIdKey) {
      return undefined;
    }
    const startDate = getAvailabilityQueryStartDate(admitDate);
    const endDate = getAvailabilityQueryEndDate(startDate);
    (async () => {
      try {
        const providerIds = providerIdKey.split(',');
        const rawSupportIds = supportIdKey ? supportIdKey.split(',') : [];

        // Remove any support IDs that are also provider IDs to prevent duplicates.
        // The availability API combines both arrays into a single "participants" list.
        // Note: This defensive check handles edge cases where a user may have multiple roles
        // (e.g., a provider who is also listed as support staff), but typically should not occur.
        const supportIds = rawSupportIds.filter(id => !providerIds.includes(id));

        await fetchProviderAvailability(
          {
            appointmentType,
            end: endDate,
            ignoreAvailabilitySettings: false,
            minimumProviderCount: 1,
            patientId,
            providerIds,
            start: startDate,
            strictAvailabilityRule: true, // Exclude providers who have not configured availability for the given appointment type
            supportIds,
          },
          true,
        ).unwrap();
      } catch (error) {
        logError(error, {
          extra: { message: 'Error fetching provider availability' },
          tags: { logFrom: 'useProviderAvailability' },
        });
      }
    })();
  }, [
    admitDate,
    appointmentType,
    fetchProviderAvailability,
    patientId,
    providerIdKey,
    skip,
    supportIdKey,
  ]);

  return { availabilityData, availableSlots, isError, isFetching };
};

export default useProviderAvailability;
